import React, { useEffect, useState } from "react";
import { TrendingUp, ShoppingBag, Users, Building, Store, IndianRupee, Loader2 } from "lucide-react";
import { BarChart, Bar, ResponsiveContainer, XAxis, YAxis, Tooltip, Cell } from "recharts";
import { api } from "../../lib/api";
import { compact, rupee } from "../../lib/format";
import { StatCard, Panel, DataTable } from "./ui";

const GOLD = "#D4AF37";

export default function CorporateDashboard() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/dashboard/corporate").then((r) => setData(r.data)); }, []);
  if (!data) return <Loading />;
  const s = data.summary;

  return (
    <div className="space-y-6">
      <Header title="Omaxe Corporate Command Centre" sub="All malls · today's first-party food court intelligence" />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={IndianRupee} label="Total Sales" value={compact(s.sales)} sub="across all malls" testid="stat-corp-sales" />
        <StatCard icon={ShoppingBag} label="Orders" value={s.orders} sub={`AOV ${rupee(s.aov)}`} accent="blue" />
        <StatCard icon={Users} label="Customers" value={s.customers} accent="emerald" />
        <StatCard icon={Building} label="Malls / Restaurants" value={`${s.mall_count} / ${s.restaurant_count}`} accent="gold" />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <Panel title="Mall Comparison">
          <DataTable testid="corp-mall-table"
            columns={[
              { key: "mall", label: "Mall" },
              { key: "city", label: "City" },
              { key: "orders", label: "Orders" },
              { key: "sales", label: "Sales", render: (r) => <span className="gold-text font-semibold">{rupee(r.sales)}</span> },
              { key: "aov", label: "AOV", render: (r) => rupee(r.aov) },
            ]}
            rows={data.malls} />
        </Panel>

        <Panel title="Top Categories (Food Court-wide)">
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={data.categories.slice(0, 7)} layout="vertical" margin={{ left: 20 }}>
              <XAxis type="number" hide />
              <YAxis type="category" dataKey="category" width={80} tick={{ fill: "#94A3B8", fontSize: 12 }} axisLine={false} tickLine={false} />
              <Tooltip cursor={{ fill: "rgba(212,175,55,0.08)" }} contentStyle={{ background: "#0E2650", border: "1px solid rgba(212,175,55,0.3)", borderRadius: 12, color: "#fff" }} formatter={(v) => rupee(v)} />
              <Bar dataKey="sales" radius={[0, 6, 6, 0]}>
                {data.categories.slice(0, 7).map((_, i) => <Cell key={i} fill={GOLD} fillOpacity={1 - i * 0.1} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Panel>
      </div>

      <Panel title="Management Information Hierarchy">
        <div className="flex flex-wrap items-center gap-2 text-xs text-slate-400">
          {["Omaxe", "Mall", "Food Court", "Restaurant Group", "Restaurant", "Category", "Item", "Order", "Customer"].map((x, i, a) => (
            <React.Fragment key={x}>
              <span className="card-surface-2 rounded-lg px-3 py-1.5 text-slate-200">{x}</span>
              {i < a.length - 1 && <span className="text-amber-500">→</span>}
            </React.Fragment>
          ))}
        </div>
      </Panel>
    </div>
  );
}

export const Header = ({ title, sub, right }) => (
  <div className="flex items-start justify-between gap-4 flex-wrap">
    <div>
      <h1 className="font-display font-extrabold text-2xl sm:text-3xl tracking-tight">{title}</h1>
      {sub && <p className="text-sm text-slate-400 mt-1">{sub}</p>}
    </div>
    {right}
  </div>
);

export const Loading = () => (
  <div className="flex items-center justify-center py-20"><Loader2 className="animate-spin text-amber-400" size={26} /></div>
);
